import React, { useCallback, useState } from 'react';
import { useBankTransactionComments, useDeleteBankTransactionComment } from '../hooks/useCommentQuery';
import { BankTransactionCommentsCreate } from './BankTransactionCommentsCreate';
import { BankTransactionCommentsEdit } from './BankTransactionCommentsEdit';
import { BankTransactionCommentsView } from './BankTransactionCommentsView';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';

export const BankTransactionCommentsSection = ({ transactionUuid, showSuccessNotification, showErrorNotification }: { transactionUuid: string, showSuccessNotification: (message: string) => void, showErrorNotification: (message: string) => void }) => {
  const { data: comments, isLoading, error } = useBankTransactionComments(transactionUuid);
  const { mutate: deleteComment, isPending: isDeleting } = useDeleteBankTransactionComment(showSuccessNotification, showErrorNotification);
  const [isAddCommentFocused, setIsAddCommentFocused] = useState(false);
  const [selectedCommentUuid, setSelectedCommentUuid] = useState<string | null>(null);

  const handleOnFocus = useCallback(() => {
    setIsAddCommentFocused(true);
  }, []);

  const handleOnBlur = useCallback(() => {
    setIsAddCommentFocused(false);
  }, []);

  const handleCommentToDelete = useCallback((commentUuid: string) => {
    setSelectedCommentUuid(commentUuid);
  }, []);

  const handleConfirmConfirmationModal = useCallback(() => {
    if (selectedCommentUuid) {
      deleteComment(
        { transactionUuid, commentUuid: selectedCommentUuid },
        {
          onSettled: () => {
            setSelectedCommentUuid(null);
          },
        }
      );
    }
  }, [deleteComment, selectedCommentUuid, transactionUuid]);

  const handleCancelConfirmationModal = useCallback(() => {
    setSelectedCommentUuid(null);
  }, []);

  // Sort comments by creation date (desc, newest first)
  const sortedComments = comments ? [...comments].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  }) : [];

  return (
    <div className="comments flex flex-col items-start gap-[20px] mt-[40px]">
      {selectedCommentUuid && (
        <ConfirmationModal
          type={EConfirmationModalType.WARNING}
          title='You are going to delete a comment.'
          message='Are you sure you want to delete this comment?'
          confirmButtonLabel="Yes"
          cancelButtonLabel="No"
          onConfirm={handleConfirmConfirmationModal}
          onCancel={handleCancelConfirmationModal}
          isConfirmLoading={isDeleting}
        />
      )}
      <p className="font-pt-sans text-black m-0 text-[16px] font-bold">Comments</p>
      {error && (
        <p className="text-red-500 text-sm">Unable to get comments</p>
      )}
      {!isAddCommentFocused && <BankTransactionCommentsCreate onFocus={handleOnFocus} />}
      {isAddCommentFocused && (
        <BankTransactionCommentsEdit onCancel={handleOnBlur} transactionUuid={transactionUuid} showSuccessNotification={showSuccessNotification} showErrorNotification={showErrorNotification} />
      )}
      {sortedComments.map(comment => (
        <BankTransactionCommentsView
          key={comment.uuid}
          comment={comment}
          handleCommentToDelete={handleCommentToDelete}
        />
      ))}
    </div>
  );
};